import styled, { css } from 'styled-components'

export const HistoryContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
`

export const FiltersBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.default};
  background: ${({ theme }) => theme.colors.background.primary};
  position: sticky;
  top: 0;
  z-index: 10;
`

export const EventFilters = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  flex: 1;
`

export const FilterPillGroup = styled.div`
  display: inline-flex;
  align-items: stretch;
`

export const SortButton = styled.button<{ $desc: boolean }>`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 5px 10px;
  border: 1px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  background: ${({ theme }) => `${theme.colors.primary}10`};
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  transition: all ${({ theme }) => theme.transitions.fast};
  flex-shrink: 0;

  svg {
    transition: transform ${({ theme }) => theme.transitions.fast};
    transform: ${({ $desc }) => ($desc ? 'rotate(0deg)' : 'rotate(180deg)')};
  }

  &:hover {
    background: ${({ theme }) => `${theme.colors.primary}20`};
  }
`

export const FilterPill = styled.button<{
  $active: boolean
  $type: string
  $hasAction?: boolean
}>`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 5px 10px;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
  font-family: inherit;

  ${({ $type, $active, theme }) => {
    // Unified colors for consistency with map layers
    const colors = {
      permit: '#805AD5',
      transaction: '#E05252',
      rental: '#00A550',
      sale: '#E68600',
    }
    const color =
      colors[$type as keyof typeof colors] || theme.colors.text.tertiary

    return css`
      border: 1px solid ${$active ? color : theme.colors.border.default};
      background: ${$active ? `${color}15` : theme.colors.background.tertiary};
      color: ${$active
        ? theme.colors.text.primary
        : theme.colors.text.tertiary};

      &:hover {
        transform: translateY(-1px);
        box-shadow: ${theme.shadows.sm};
        background: ${$active
          ? `${color}25`
          : theme.colors.background.secondary};
        border-color: ${color};
      }

      &:active {
        transform: translateY(0);
      }
    `
  }}

  ${({ $hasAction }) =>
    $hasAction &&
    css`
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
      border-right-width: 0;
    `}
`

export const FilterActionButton = styled.button<{
  $active: boolean
  $type: string
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  border-radius: 0 ${({ theme }) => theme.borderRadius.medium}
    ${({ theme }) => theme.borderRadius.medium} 0;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  font-family: inherit;

  ${({ $type, $active, theme }) => {
    const colors = {
      permit: '#805AD5',
      transaction: '#E05252',
      rental: '#00A550',
      sale: '#E68600',
    }
    const color =
      colors[$type as keyof typeof colors] || theme.colors.text.tertiary

    return css`
      border: 1px solid ${$active ? color : theme.colors.border.default};
      background: ${$active ? `${color}15` : theme.colors.background.tertiary};
      color: ${$active ? color : theme.colors.text.tertiary};

      &:hover:not(:disabled) {
        transform: translateY(-1px);
        box-shadow: ${theme.shadows.sm};
        background: ${$active
          ? `${color}25`
          : theme.colors.background.secondary};
        border-color: ${color};
      }

      &:active:not(:disabled) {
        transform: translateY(0);
      }

      &:disabled {
        cursor: not-allowed;
        opacity: 0.45;
      }
    `
  }}
`

export const FilterIcon = styled.span`
  font-size: 13px;
  line-height: 1;
`

export const FilterCount = styled.span<{ $active: boolean }>`
  background: ${({ theme, $active }) =>
    $active ? theme.colors.text.primary : theme.colors.text.tertiary};
  color: ${({ theme }) => theme.colors.text.inverse};
  padding: 2px 5px;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  font-size: 10px;
  font-weight: 600;
  min-width: 16px;
  text-align: center;
  transition: all ${({ theme }) => theme.transitions.fast};
`

export const FilterSinceYear = styled.span`
  font-size: 8px;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.text.tertiary};
  margin-left: 2px;
`

export const TimelineContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 16px;
  background: linear-gradient(
    to bottom,
    ${({ theme }) => theme.colors.background.secondary} 0%,
    ${({ theme }) => theme.colors.background.primary} 100%
  );
`

export const Timeline = styled.div`
  position: relative;
  padding-left: 24px;

  &::before {
    content: '';
    position: absolute;
    left: 8px;
    top: 8px;
    bottom: 8px;
    width: 2px;
    background: linear-gradient(
      to bottom,
      ${({ theme }) => theme.colors.border.default},
      transparent
    );
  }
`

export const TimelineEvent = styled.div<{
  $type: string
  $visible: boolean
}>`
  position: relative;
  margin-bottom: 20px;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transform: translateX(${({ $visible }) => ($visible ? '0' : '-20px')});
  transition:
    opacity ${({ theme }) => theme.transitions.default},
    transform ${({ theme }) => theme.transitions.default};
  animation: fadeIn ${({ theme }) => theme.transitions.default} ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateX(-20px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
`

export const TimelineMarker = styled.div<{ $type: string }>`
  position: absolute;
  left: -21px;
  top: 6px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 2px solid #ffffff;
  box-shadow: ${({ theme }) => theme.shadows.md};

  ${({ $type, theme }) => {
    // Unified colors for consistency with map layers
    const colors = {
      permit: '#805AD5',
      transaction: '#E05252',
      rental: '#00A550',
      sale: '#E68600',
    }
    const color =
      colors[$type as keyof typeof colors] || theme.colors.text.tertiary

    return css`
      background: ${color};
    `
  }}
`

export const EventDate = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 6px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
`

export const EventIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  border-radius: ${({ theme }) => theme.borderRadius.circle};
`

export const EventCard = styled.div<{ $type: string }>`
  background: ${({ theme }) => theme.colors.background.primary};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  padding: 12px;
  box-shadow: ${({ theme }) => theme.shadows.sm};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    box-shadow: ${({ theme }) => theme.shadows.md};
    transform: translateY(-2px);
  }

  ${({ $type, theme }) => {
    // Unified colors for consistency with map layers
    const colors = {
      permit: '#805AD5',
      transaction: '#E05252',
      rental: '#00A550',
      sale: '#E68600',
    }
    const color =
      colors[$type as keyof typeof colors] || theme.colors.text.tertiary

    return css`
      ${EventIcon} {
        color: ${color};
        background: ${color}1A;
      }
    `
  }}
`

export const EventHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
`

export const EventTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`

export const EventStatus = styled.span<{ $status?: string }>`
  padding: 3px 6px;
  background: ${({ theme, $status }) => {
    const statusLower = $status?.toLowerCase()
    if (statusLower === 'active') return theme.colors.successLight
    if (statusLower === 'inactive') return theme.colors.background.tertiary
    if (statusLower === 'pending') return theme.colors.warningLight
    return theme.colors.background.tertiary
  }};
  color: ${({ theme, $status }) => {
    const statusLower = $status?.toLowerCase()
    if (statusLower === 'active') return theme.colors.success
    if (statusLower === 'inactive') return theme.colors.text.secondary
    if (statusLower === 'pending') return theme.colors.warning
    return theme.colors.text.secondary
  }};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

export const EventContent = styled.div`
  display: flex;
  gap: 12px;
`

export const EventDetails = styled.div`
  flex: 1;
`

export const EventImage = styled.div`
  width: 96px;
  height: 72px;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  overflow: hidden;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

export const EventPrice = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 3px;
`

export const EventPriceSub = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.tertiary};
  margin-bottom: 6px;
`

export const EventDescription = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.5;
  margin-bottom: 10px;
`

export const EventMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`

export const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 10px;
  color: ${({ theme }) => theme.colors.text.secondary};
`

export const MetaIcon = styled.svg`
  width: 11px;
  height: 11px;
  stroke: currentColor;
  stroke-width: 2;
  fill: none;
  stroke-linecap: round;
  stroke-linejoin: round;
`

export const EventFooter = styled.div`
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid ${({ theme }) => theme.colors.border.default};
  font-size: 10px;
  color: ${({ theme }) => theme.colors.text.tertiary};
`

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 38px 20px;
  text-align: center;
  min-height: 240px;
`

export const EmptyIcon = styled.div`
  font-size: 38px;
  margin-bottom: 12px;
  opacity: 0.5;
`

export const EmptyTitle = styled.h3`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 6px 0;
`

export const EmptyText = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.tertiary};
  margin: 0;
  max-width: 240px;
`

export const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 38px 20px;
  min-height: 240px;
`

export const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 38px 20px;
  text-align: center;
  min-height: 240px;
`

export const ErrorTitle = styled.h3`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.danger};
  margin: 0 0 6px 0;
`

export const ErrorText = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
  max-width: 320px;
`

export const EventWrapper = styled.div`
  cursor: pointer;
  transition: transform ${({ theme }) => theme.transitions.fast};

  &:hover {
    transform: translateX(4px);
  }
`
